"use client"

import * as React from "react"
import { Label, Pie, PieChart } from "recharts"

import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "../../../shared/components/ui/card"
import {
    ChartConfig,
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "../../../shared/components/ui/chart"

const chartConfig = {
    todo: {
        label: "To Go",
        color: "green",
    },
    completed: {
        label: "Completed",
        color: "blue",
    },
    // visitors: {
    //     label: "Visitors",
    // },
    // chrome: {
    //     label: "Chrome",
    //     color: "hsl(var(--chart-1))",
    // },
    // safari: {
    //     label: "Safari",
    //     color: "hsl(var(--chart-2))",
    // },
    // firefox: {
    //     label: "Firefox",
    //     color: "hsl(var(--chart-3))",
    // },
    // edge: {
    //     label: "Edge",
    //     color: "hsl(var(--chart-4))",
    // },
    // other: {
    //     label: "Other",
    //     color: "hsl(var(--chart-5))",
    // },
} satisfies ChartConfig

type ReportPieChartProps = {
    title: string,
    numerator?: number,
    denominator?: number,
    percentage?: number,
    chartData?: Array<{ key: string, value: number, fill: string }>,
    chartConfig?: ChartConfig,
}

export default function ReportPieChart({ 
    title, 
    numerator = 172, 
    denominator = 176, 
    percentage = 98,
    chartData = [
        { key: 'todo', value: 180, fill: "#D4A017" },
        { key: 'completed', value: 180, fill: "#3E7C59" },
    ],
    chartConfig: customChartConfig
}: ReportPieChartProps) {

    const finalChartConfig = customChartConfig || chartConfig;

    return (
        <Card className="flex flex-col bg-transparent p-0 border-none shadow-none">
            <CardHeader className="items-center pb-0 p-0">
                <CardTitle>{title}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2 pb-0 pt-0 -mt-5 p-0">
                <ChartContainer
                    config={finalChartConfig}
                    className="mx-auto aspect-square h-[280px] w-[280px] max-h-[280px] max-w-[280px] shrink-0 [&_.recharts-responsive-container]:!h-full [&_.recharts-responsive-container]:!w-full"
                >
                    <PieChart>
                        <ChartTooltip
                            cursor={false}
                            content={<ChartTooltipContent hideLabel className="min-w-[12rem]" />}
                        />
                        <Pie
                            data={chartData}
                            dataKey="value"
                            nameKey="key"
                            innerRadius={60}
                            strokeWidth={5}
                        >
                            <Label
                                content={({ viewBox }) => {
                                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                                        const textX = viewBox.cx as number;
                                        const textY = viewBox.cy as number;
                                        return (
                                            <svg>
                                                <text
                                                    x={textX}
                                                    y={textY}
                                                    textAnchor="middle"
                                                    dominantBaseline="middle"
                                                >
                                                    <tspan
                                                        x={textX}
                                                        y={textY}
                                                        className="fill-foreground text-xs font-bold"
                                                    >
                                                        {`${numerator}/${denominator} (${percentage}%)`}
                                                    </tspan>
                                                </text>
                                            </svg>
                                        )
                                    }
                                }}
                            />
                        </Pie>
                    </PieChart>
                </ChartContainer>
                <div
                    className="flex flex-wrap justify-center gap-x-4 gap-y-2 px-1 pb-1 [&>*]:flex-shrink-0"
                    aria-label="Chart legend"
                >
                    {chartData.map((item) => {
                        const itemConfig =
                            finalChartConfig[item.key as keyof typeof finalChartConfig]
                        return (
                            <div
                                key={item.key}
                                className="flex items-center gap-1.5 text-xs [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground"
                            >
                                <div
                                    className="h-2 w-2 shrink-0 rounded-[2px]"
                                    style={{ backgroundColor: item.fill }}
                                />
                                <span className="text-muted-foreground">
                                    {itemConfig?.label ?? item.key}
                                </span>
                            </div>
                        )
                    })}
                </div>
            </CardContent>
        </Card>
    )
}
